/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */

import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { atomFamily, selectorFamily, useRecoilCallback, atom, DefaultValue, useRecoilValue, useSetRecoilState } from "recoil";

import {
  User,
  Property,
  Image,
  LeaseContract,
  MonthlyCashflowResult,
  SimulationConfigWithId,
  RegisteredStatusEnum,
  UserRegistrations,
  JobCategory,
  UserRegistrationsGoogle,
  RegistrationMethod,
  UserRegistrationsApple,
  BuildingRoom,
  LearningArticleStatus,
  WebinarSummary,
} from "@/api/generated/api";
import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA } from "@/constants/account";
import { DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA } from "@/constants/addNewProperty";
import { AddNewPropertyFormSchema } from "@/types/addNewProperty";
import {
  getStorageAccountRegistrationState,
  removeStorageAccountRegistrationState,
  setStorageAccountRegistrationState,
  setStorageConsiderationPropertyRegistrationState,
  setStoragePropertyRegistrationState,
  setStorageOwnBuildingPropertyRegistrationState,
} from "@/utilities/storage";
import { AccountRegisterFormData } from "@/validations/account";

import { Properties } from "./propertySelectors";

export type Session = {
  logged_in: boolean;
};

export const SessionState = atom<Session>({
  key: "SessionState",
  default: {
    logged_in: false,
  },
});

// conditional flag for React.useEffect() on initial loading.
export const RequireLoadingFromBackendState = atom<number>({
  key: "require_loading_from_backend",
  default: 0,
});

export const useRequestInitialLoading = () => {
  const update_atom = useRecoilCallback(({ snapshot, set }) => async () => {
    let require_loading = await snapshot.getPromise(RequireLoadingFromBackendState);
    set(RequireLoadingFromBackendState, ++require_loading);
  });
  return update_atom;
};

export const UserState = atom<User>({
  key: "UserState",
  default: null,
});

export const UserDataFirstRegisteredStatusState = atom<RegisteredStatusEnum>({
  key: "UserDataFirstRegisteredStatusState",
  default: RegisteredStatusEnum.Registration,
});

export type ImageHolder = {
  image: Image;
  url: string;
};

export type PropertyHolder = {
  id: number;
  property: Property;
  images: Array<ImageHolder>;
  lease_contracts: Array<LeaseContract>;
  building_room: Array<BuildingRoom>;
  simulation_configs: SimulationConfigWithId[];
  cover_image_url: string;
  user_input_monthly_cashflow_results: Array<MonthlyCashflowResult>;
};

export const PropertyIds = atom<Array<number>>({
  key: "property_ids",
  default: [],
});

// Note: the following state is loaded from API backend.
// DO NOT use this state in your ReactDOM component directly:
// this state holds properties as concatenated single array and thus it causes huge performance impact.
// Instead, use the following `PropertyHoldersState` atom family.
export const FetchedPropertyHolders = atom<Array<PropertyHolder>>({
  key: "initial_property_holders",
  default: [],
});

// 初回ロード中であることを示すフラグ
export const BackendLoadedState = atom<boolean>({
  key: "backend_loaded_state",
  default: false,
});

export const PropertyHoldersState = atomFamily<PropertyHolder, number>({
  key: "property_holders",
  default: selectorFamily({
    key: "property_holders/default",
    get:
      (id) =>
      ({ get }) => {
        const fetched = get(FetchedPropertyHolders);
        const property = fetched.find((e) => {
          return e.id === id;
        });
        return property;
      },
  }),
});

export const SelectedPropertyId = atom<number | null>({
  key: "selected_property_id",
  default: null,
});

export const SelectedPropertyIds = atom<Array<number>>({
  key: "selected_property_ids",
  default: [],
});

export type PropertyTourSeen = {
  singleRoom: boolean; // 区分マンション
  multipleRoom: boolean; // 一棟物件
};

// 物件詳細のツアーを見たかどうかの状態を管理
export const PropertyTourSeenState = atom<PropertyTourSeen>({
  key: "property_tour_seen_state",
  default: {
    singleRoom: false,
    multipleRoom: false,
  },
  effects_UNSTABLE: [
    ({ setSelf, onSet }) => {
      const storageKey = "propally:property_tour_seen:v1";
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          const raw = window.localStorage.getItem(storageKey);
          if (raw) setSelf(JSON.parse(raw));
        }
      } catch (_) {
        // noop
      }

      onSet((newValue) => {
        try {
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem(storageKey, JSON.stringify(newValue));
          }
        } catch (_) {
          // noop
        }
      });
    },
  ],
});

export type SellSimulationState = {
  enable: boolean;
  sell_at: string;
  price: number;
};

export type PriceSimulationState = {
  enablePriceSimulationFromProposal: boolean;
  enableBalanceSimulationFromProposal: boolean;
};

export const PropertySellSimulationState = atomFamily<SellSimulationState, number>({
  key: "property_sell_simulation_state",
  default: {
    enable: false,
    sell_at: dayjs().format("YYYY/MM"),
    price: 0,
  },
});

export const DisabledPriceSimulation = {
  enablePriceSimulationFromProposal: false,
  enableBalanceSimulationFromProposal: false,
} as const;

export const PropertyPriceSimulationState = atomFamily<PriceSimulationState, number>({
  key: "property_price_simulation_state",
  default: DisabledPriceSimulation,
});

export const useSelectAllProperties = (): Function => {
  const property_ids = useRecoilValue(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(null);
    setSelectedPropertyIds(property_ids);
  };
};

export const useSelectFirstProperty = (): Function => {
  const property_ids = useRecoilValue(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(property_ids[0]);
    setSelectedPropertyIds([property_ids[0]]);
  };
};

export const useSelectLimitedProperties = (limit: number): Function => {
  const property_ids = useRecoilValue(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(property_ids[0]);
    setSelectedPropertyIds(property_ids.slice(0, limit));
  };
};

export const useSelectOwnedProperties = (): Function => {
  const properties = useRecoilValue(Properties);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(null);
    setSelectedPropertyIds(
      properties
        .filter((property) => {
          return (
            property.property.property_type === PROPERTY_TYPE_VALUES.owned ||
            property.property.property_type === PROPERTY_TYPE_VALUES.owned_sample
          );
        })
        .map((p) => {
          return p.id;
        })
    );
  };
};

export { SelectableMinMonth, SelectableMaxMonth } from "./selectableMonths";
export const SelectedStartingMonth = atom<Dayjs>({
  key: "SelectedStartingMonth",
  default: dayjs().startOf("year"),
});

export const SelectedEndingMonth = atom<Dayjs>({
  key: "SelectedEndingMonth",
  default: dayjs().endOf("year").add(45, "year"),
});

/**
 * /balance 画面専用の期間（他画面のシミュレーション等と分離する）
 * - 収支表/確定申告は当年など短期表示が多く、45年レンジだと計算が重すぎる
 * - マイページ/物件詳細のシミュレーションは従来通り SelectedStarting/EndingMonth を利用する
 */
export const BalanceSelectedStartingMonth = atom<Dayjs>({
  key: "BalanceSelectedStartingMonth",
  default: dayjs().startOf("year"),
});

export const BalanceSelectedEndingMonth = atom<Dayjs>({
  key: "BalanceSelectedEndingMonth",
  default: dayjs().endOf("year"),
});
export const ComparisonTargetMonth = atom<Dayjs>({
  key: "ComparisonTargetMonth",
  default: dayjs(),
});

export const RegistrationMethodState = atom<RegistrationMethod>({
  key: "RegistrationMethodState",
  default: RegistrationMethod.Email,
});

export const UserRegistrationsFormState = atom<UserRegistrations>({
  key: "UserRegistrationsFormState",
  default: {
    family_name: "",
    first_name: "",
    family_name_kana: "",
    first_name_kana: "",
    email: "",
    phone_number: "",
    birthday: "",
    job_category: JobCategory.その他,
    request_the_proposal: false,
    password: "",
    password_confirmation: "",
    phone_number_auth_code: 0,
  },
});

export const UserRegistrationsGoogleFormState = atom<UserRegistrationsGoogle>({
  key: "UserRegistrationsGoogleFormState",
  default: {
    social_login_response: null,
    phone_number: "",
    phone_number_auth_code: 0,
  },
});

export const UserRegistrationsAppleFormState = atom<UserRegistrationsApple>({
  key: "UserRegistrationsAppleFormState",
  default: {
    social_login_response: null,
    phone_number: "",
    phone_number_auth_code: 0,
  },
});

export const UserRegistrationsAccoutFormState = atom<AccountRegisterFormData>({
  key: "UserRegistrationsAccoutFormState",
  default: DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet, setSelf, trigger }) => {
      if (trigger === "get") {
        const saved = getStorageAccountRegistrationState();
        if (saved) setSelf({ ...DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA, ...saved });
      }
      onSet((newValue: AccountRegisterFormData) => {
        if (newValue instanceof DefaultValue) removeStorageAccountRegistrationState();
        else setStorageAccountRegistrationState(newValue);
      });
    },
  ],
});

export type LearningArticleStatusMap = Record<string, LearningArticleStatus>;

export const LearningArticleStatusState = atom<LearningArticleStatusMap>({
  key: "LearningArticleStatusState",
  default: {},
});

export const PremiumWebinarHighlightsState = atom<WebinarSummary[]>({
  key: "PremiumWebinarHighlightsState",
  default: [],
});

// 物件登録 - 保有物件 - 区分
export const PropertyRegistrationState = atom<AddNewPropertyFormSchema>({
  key: "PropertyRegistrationState",
  default: DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newValue: AddNewPropertyFormSchema) => {
        // LocalStorageに保存
        setStoragePropertyRegistrationState(newValue);
      });
    },
  ],
});

// 物件登録 - 保有物件 - 一棟
export const OwnBuildingPropertyRegistrationState = atom<AddNewPropertyFormSchema>({
  key: "OwnBuildingPropertyRegistrationState",
  default: DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newValue: AddNewPropertyFormSchema) => {
        // LocalStorageに保存
        setStorageOwnBuildingPropertyRegistrationState(newValue);
      });
    },
  ],
});

// 物件登録 - 検討中物件
export const ConsiderationPropertyRegistrationState = atom<AddNewPropertyFormSchema>({
  key: "ConsiderationPropertyRegistrationState",
  default: DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newValue: AddNewPropertyFormSchema) => {
        // LocalStorageに保存
        setStorageConsiderationPropertyRegistrationState(newValue);
      });
    },
  ],
});

// 検討中物件 - AIおまかせ入力を利用したか（計測用）
export const ConsiderationPropertyAiInputUsedState = atom<boolean>({
  key: "consideration_property_ai_input_used_state",
  default: false,
});

// 物件一覧画面のスナックバーに表示するメッセージ
export type ManagementListSnackbar = {
  message: string;
  status: "success" | "error" | "";
  isOpen: boolean;
};

export const PropertyManagementListSnackbarState = atom<ManagementListSnackbar>({
  key: "property_management_list_snackbar_state",
  default: {
    message: "",
    status: "",
    isOpen: false,
  },
});

// 物件詳細画面のスナックバーに表示するメッセージ
export type ManagementDetailSnackbar = {
  message: string;
  status: "success" | "error" | "";
  isOpen: boolean;
};

export const PropertyManagementDetailSnackbarState = atom<ManagementDetailSnackbar>({
  key: "property_management_detail_snackbar_state",
  default: {
    message: "",
    status: "",
    isOpen: false,
  },
});

export const PropertyEditPageTypes = {
  property_info: "property_info", // 物件詳細
  purchase_info: "purchase_info", // 購入時情報
  loan_info: "loan_info", // 借入情報
  management_cost: "management_cost", // 賃貸管理費 経費情報
  management_building_cost: "management_building_cost", // 一棟物件の賃貸管理費 経費情報
  lease_contract: "lease_contract", // 賃貸契約情報
  insurance_info: "insurance_info", // 保険情報
  building_room: "building_room", // 一棟物件の部屋情報
} as const;

// 物件編集の表示画面切り替え
export const PropertyEditPageState = atom<keyof typeof PropertyEditPageTypes | null>({
  key: "property_edit_page_state",
  default: null,
});

// 累計既読件数
export const ReadOffersCountState = atom<number>({
  key: "read_offers_count_state",
  default: 0,
});

// マッチング件数
export const MatchedOffersCountState = atom<number>({
  key: "matched_offers_count_state",
  default: 0,
});

// 未読アナウンスの有無
export const HasUnreadAnnouncementState = atom<boolean>({
  key: "has_unread_announcement_state",
  default: false,
});

// アップデート促進モーダル表示の有無
export const NeedShowUpdateModalState = atom<boolean>({
  key: "need_show_update_modal_state",
  default: false,
});
